<div class="container-fluid complyon-page risks-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell risks-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title risks-title mb-2 text-l">Risks</h1>
      <p class="complyon-subtitle risks-subtitle mb-0 text-s">Risk register, mitigation plans, control mapping and escalation.</p>
    </section>

    <section class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn doa-reset-btn d-inline-flex align-items-center gap-1">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'register'">
      <section class="row g-3 mb-3">
        <div v-for="card in register_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card risks-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label risks-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value risks-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'">{{ card.value }}</div>
            <div class="complyon-stat-note risks-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel risks-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Risk register</div>
            <div class="complyon-section-subtitle text-xs">Inherent - residual scoring, owner, linked controls and movement. Open one for the full profile.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_risk_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New risk</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table risks-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Inherent</th>
                <th class="text-xs">Residual</th>
                <th class="text-xs">Trend</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in risk_rows"
                :key="row.ref"
                class="risks-register-row"
                role="button"
                tabindex="0"
                @click="open_risk_drawer(row)"
                @keydown.enter.prevent="open_risk_drawer(row)"
                @keydown.space.prevent="open_risk_drawer(row)"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td><div class="risks-name">{{ row.risk }}</div></td>
                <td>{{ row.category }}</td>
                <td>{{ row.owner }}</td>
                <td class="fw-semibold">{{ row.inherent }}</td>
                <td>
                  <span class="badge rounded-3 bg-light text-secondary border border-light fw-semibold">{{ row.residual }}</span>
                </td>
                <td>
                  <svg width="58" height="18" viewBox="0 0 58 18" aria-hidden="true">
                    <path d="M2 4 L18 5 L30 8 L40 9 L56 14" fill="none" stroke="#13856f" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="56" cy="14" r="1.8" fill="#13856f" />
                  </svg>
                </td>
                <td><span class="badge complyon-badge risks-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'heatmap'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="px-3 pt-3">
          <div class="complyon-section-title text-s">Risk heatmap</div>
          <div class="complyon-section-subtitle text-xs">Residual risk by likelihood × impact. Click a cell to open its risks.</div>
        </div>

        <div class="px-3 pb-3 pt-3 risks-heatmap-wrap">
          <div class="risks-heatmap-shell">
            <div class="risks-heatmap-axis risks-heatmap-axis--y">Impact →</div>
            <div class="risks-heatmap-main">
              <div class="risks-heatmap-grid">
                <template v-for="(row, rowIndex) in heatmap_rows" :key="`heat-row-${rowIndex}`">
                  <div class="risks-heatmap-row-label">{{ row.label }}</div>
                  <div
                    v-for="(cell, colIndex) in row.cells"
                    :key="`heat-${rowIndex}-${colIndex}`"
                    class="risks-heatmap-cell"
                    :class="[`is-${cell.tone}`, { 'is-clickable': cell.items.length === 1 }]"
                    @click="cell.items.length === 1 ? open_risk_drawer(cell.items[0]) : null"
                  >
                    <div class="risks-heatmap-cell-inner">
                      <div class="risks-heatmap-items">
                        <button
                          v-for="item in cell.items"
                          :key="item"
                          type="button"
                          class="btn btn-link p-0 text-decoration-none risks-heatmap-item"
                          @click.stop="open_risk_drawer(item)"
                        >
                          {{ item }}
                        </button>
                      </div>
                      <span v-if="cell.count" class="risks-heatmap-count">{{ cell.count }}</span>
                    </div>
                  </div>
                </template>
              </div>

              <div class="risks-heatmap-x-labels">
                <span v-for="label in heatmap_likelihood_labels" :key="label">{{ label }}</span>
              </div>
              <div class="risks-heatmap-axis risks-heatmap-axis--x">Likelihood →</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'appetite'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="px-3 pt-3">
          <div class="complyon-section-title text-s">Risk appetite</div>
          <div class="complyon-section-subtitle text-xs">Tolerance per category vs the highest residual risk in it. Adjust appetite with - / +.</div>
        </div>

        <div class="px-3 pb-3">
          <div v-for="row in appetite_rows" :key="row.area" class="risks-appetite-row d-flex align-items-center gap-3 py-3 border-bottom">
            <div class="risks-appetite-area fw-semibold">{{ row.area }}</div>
            <div class="risks-appetite-bar-wrap flex-grow-1 d-flex align-items-center gap-2">
              <div class="risks-appetite-track rounded-1 overflow-hidden flex-grow-1">
                <div class="risks-appetite-fill h-100 rounded-1" :class="{ 'is-danger': row.peak > row.appetite }" :style="{ width: `${row.width}%` }"></div>
              </div>
              <div class="risk-appetite-marker" :style="{ left: `${row.marker_position}%` }"></div>
            </div>
            <div class="risks-appetite-meta text-xs text-nowrap" :class="row.peak > row.appetite ? 'is-danger' : 'is-success'">peak {{ row.peak }} / appetite {{ row.appetite }}</div>
            <div class="risks-appetite-actions d-flex align-items-center gap-2">
              <button type="button" class="btn btn-link text-decoration-none p-0 risks-appetite-btn" @click="decrease_peak(row.area)" aria-label="Decrease peak">-</button>
              <button type="button" class="btn btn-link text-decoration-none p-0 risks-appetite-btn" @click="increase_appetite(row.area)" aria-label="Increase appetite">+</button>
            </div>
            <div class="text-secondary">−</div>
            <div class="text-secondary">+</div>
            <span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'by-department'">
      <section class="row g-3">
        <div v-for="dept in department_cards" :key="dept.dept" class="col-12 col-md-6 col-xl-4">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="complyon-section-title text-s">{{ dept.dept }}</div>
              <span class="badge rounded-pill" :class="dept.badge === 'Low' ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'">{{ dept.badge }}</span>
            </div>

            <div class="mt-2 d-flex align-items-center gap-2">
              <div class="complyon-stat-value text-l">{{ dept.count }}</div>
              <div class="text-xs">risks</div>
              <div class="text-xs text-secondary">• peak residual</div>
              <span class="badge rounded-3 bg-light text-secondary border border-light">{{ dept.peak }}</span>
              <div v-if="dept.escalated" class="text-xs text-danger">{{ dept.escalated }} escalated</div>
            </div>

            <div class="mt-3">
              <div
                v-for="item in dept.rows"
                :key="`${dept.dept}-${item.label}`"
                class="d-flex align-items-center justify-content-between gap-3 py-2 risks-dept-item"
                role="button"
                tabindex="0"
                @click="open_risk_by_label(item.label)"
                @keydown.enter.prevent="open_risk_by_label(item.label)"
                @keydown.space.prevent="open_risk_by_label(item.label)"
              >
                <div class="d-flex align-items-center gap-2 min-w-0">
                  <span class="rounded-circle flex-shrink-0" :style="{ width: '8px', height: '8px', backgroundColor: item.color === 'green' ? '#13856f' : '#c75a06' }"></span>
                  <div class="text-truncate">{{ item.label }}</div>
                </div>
                <div class="fw-semibold text-xs">{{ item.value }}</div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>

  <div v-if="active_risk_drawer" class="command-centre-drawer-backdrop" @click="close_risk_drawer"></div>

  <aside
    v-if="active_risk_drawer"
    class="command-centre-drawer risks-detail-drawer"
    role="dialog"
    aria-modal="true"
    :aria-label="active_risk_drawer.title"
    @click.stop
  >
    <header class="command-centre-drawer-header border-bottom risks-drawer-header">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="command-centre-drawer-kicker risks-drawer-kicker text-uppercase">{{ active_risk_drawer.section }}</div>
          <h2 class="command-centre-drawer-title risks-drawer-title mt-2 mb-1">{{ active_risk_drawer.title }}</h2>
          <div class="command-centre-drawer-subtitle risks-drawer-subtitle mt-1">{{ active_risk_drawer.subtitle }}</div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="badge rounded-pill" :class="drawer_status_class(active_risk_drawer.status)">{{ active_risk_drawer.status }}</span>
          <button type="button" class="btn btn-link text-decoration-none p-0 command-centre-drawer-close risks-drawer-close" @click="close_risk_drawer" aria-label="Close drawer">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
    </header>

    <div ref="riskDrawerBody" class="command-centre-drawer-body risks-drawer-body">
      <section class="risks-drawer-score-grid mb-4">
        <article class="risks-drawer-score-card" :class="`is-${active_risk_drawer.stats.inherent.tone}`">
          <div class="risks-drawer-score-label">{{ active_risk_drawer.stats.inherent.label }}</div>
          <div class="risks-drawer-score-value">{{ active_risk_drawer.stats.inherent.value }}</div>
          <div class="risks-drawer-score-note">{{ active_risk_drawer.stats.inherent.note }}</div>
        </article>

        <div class="risks-drawer-score-arrow">
          <div class="risks-drawer-score-arrow-icon">&rarr;</div>
          <div class="risks-drawer-score-arrow-copy">Controls reduce</div>
        </div>

        <article class="risks-drawer-score-card" :class="`is-${active_risk_drawer.stats.residual.tone}`">
          <div class="risks-drawer-score-label">{{ active_risk_drawer.stats.residual.label }}</div>
          <div class="risks-drawer-score-value">{{ active_risk_drawer.stats.residual.value }}</div>
          <div class="risks-drawer-score-note">{{ active_risk_drawer.stats.residual.note }}</div>
        </article>

        <article class="risks-drawer-score-card is-neutral">
          <div class="risks-drawer-score-label">{{ active_risk_drawer.stats.trend.label }}</div>
          <svg class="risks-drawer-trend" viewBox="0 0 92 40" aria-hidden="true">
            <path :d="active_risk_drawer.stats.trend.path" fill="none" stroke="#13856f" stroke-width="2" stroke-linecap="round" />
            <circle cx="82" cy="32" r="2.4" fill="#13856f" />
          </svg>
          <div class="risks-drawer-score-note">{{ active_risk_drawer.stats.trend.note }}</div>
        </article>
      </section>

      <section class="mb-4">
        <div class="command-centre-drawer-section-title mb-2">Connected records</div>
        <div class="row g-2">
          <div v-for="record in active_risk_drawer.connected_records" :key="`${record.type}-${record.title}`" class="col-12 col-sm-6">
            <article class="risks-drawer-record h-100">
              <div class="d-flex align-items-start justify-content-between gap-2">
                <div class="d-flex align-items-start gap-2 min-w-0">
                  <span class="risks-drawer-record-icon" :class="drawer_tone_class(record.tone)">
                    <i class="bi" :class="drawer_icon_for_type(record.type)"></i>
                  </span>
                  <div class="min-w-0">
                    <div class="risks-drawer-record-type">{{ record.type }}</div>
                    <div class="risks-drawer-record-title">{{ record.title }}</div>
                  </div>
                </div>
                <i class="bi bi-box-arrow-up-right risks-drawer-record-link"></i>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="mb-4">
        <div class="command-centre-drawer-section-title mb-2">Controls reducing this risk</div>
        <div v-if="active_risk_drawer.controls.length" class="d-flex flex-column gap-2">
          <article v-for="control in active_risk_drawer.controls" :key="control.title" class="risks-drawer-list-card">
            <div class="d-flex align-items-start gap-2">
              <span class="risks-drawer-record-icon is-green">
                <i class="bi bi-shield-check"></i>
              </span>
              <div class="min-w-0">
                <div class="risks-drawer-list-title">{{ control.title }}</div>
                <div class="risks-drawer-list-subtitle">{{ control.subtitle }}</div>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="risks-drawer-empty-note">No controls linked &mdash; residual = inherent until a control is in place.</div>
      </section>

      <section>
        <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
          <div class="command-centre-drawer-section-title mb-0">Mitigation actions</div>
          <button type="button" class="btn btn-link text-decoration-none p-0 risks-drawer-add-btn">
            <i class="bi bi-plus-lg"></i>
            <span>Add</span>
          </button>
        </div>
        <div class="d-flex flex-column gap-2">
          <article v-for="action in active_risk_drawer.mitigation_actions" :key="action.title" class="risks-drawer-list-card">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="d-flex align-items-start gap-2 min-w-0">
                <span class="risks-drawer-record-icon is-orange">
                  <i class="bi bi-pencil-square"></i>
                </span>
                <div class="min-w-0">
                  <div class="risks-drawer-list-title">{{ action.title }}</div>
                  <div class="risks-drawer-list-subtitle">{{ action.subtitle }}</div>
                </div>
              </div>
              <span class="badge rounded-pill flex-shrink-0" :class="action_status_class(action.status)">{{ action.status }}</span>
            </div>
          </article>
        </div>
      </section>
    </div>

    <footer class="command-centre-drawer-footer border-top risks-drawer-footer">
      <button v-if="active_risk_drawer.can_escalate" type="button" class="btn btn-link text-decoration-none p-0 risks-drawer-footer-link" @click="escalate_active_risk">
        <i class="bi bi-arrow-up"></i>
        <span>Escalate</span>
      </button>
      <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2 ms-auto" @click="edit_active_risk">
        <i class="bi bi-gear"></i>
        <span>Edit risk</span>
      </button>
    </footer>
  </aside>

  <div v-if="show_risk_modal" class="risks-modal-backdrop" @click.self="close_risk_modal">
    <div class="risks-modal-shell" role="dialog" aria-modal="true" aria-label="New risk">
      <div class="risks-modal-header">
        <div>
          <h3 class="risks-modal-title mb-2">New risk</h3>
          <p class="risks-modal-subtitle mb-0">Score = likelihood &times; impact (1&ndash;25). Controls and mitigations are managed in the risk detail.</p>
        </div>
        <button type="button" class="risks-modal-close" @click="close_risk_modal" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="risks-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="risks-modal-label">Risk</label>
            <input v-model="risk_form.risk" type="text" class="risks-modal-input" placeholder="e.g. Third-party data breach">
          </div>

          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Category</label>
            <select v-model="risk_form.category" class="risks-modal-input risks-modal-select">
              <option v-for="option in risk_categories" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Department</label>
            <input v-model="risk_form.department" type="text" class="risks-modal-input" placeholder="e.g. Finance">
          </div>

          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Risk owner</label>
            <input v-model="risk_form.owner" type="text" class="risks-modal-input" placeholder="Role &middot; name">
          </div>
          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Risk review</label>
            <input v-model="risk_form.review" type="text" class="risks-modal-input" placeholder="e.g. Quarterly &middot; next Jul 2026">
          </div>

          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Status</label>
            <select v-model="risk_form.status" class="risks-modal-input risks-modal-select">
              <option v-for="option in risk_statuses" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>

        <div class="risks-modal-section mt-3">
          <div class="risks-modal-section-title">Inherent (before controls)</div>
        </div>
        <div class="row g-3 mt-0">
          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Likelihood</label>
            <select v-model.number="risk_form.inherent_likelihood" class="risks-modal-input risks-modal-select">
              <option v-for="option in likelihood_options" :key="`inherent-likelihood-${option.value}`" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Impact</label>
            <select v-model.number="risk_form.inherent_impact" class="risks-modal-input risks-modal-select">
              <option v-for="option in impact_options" :key="`inherent-impact-${option.value}`" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
        </div>

        <div class="risks-modal-section mt-3">
          <div class="risks-modal-section-title">Residual (after controls)</div>
        </div>
        <div class="row g-3 mt-0">
          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Likelihood</label>
            <select v-model.number="risk_form.residual_likelihood" class="risks-modal-input risks-modal-select">
              <option v-for="option in likelihood_options" :key="`residual-likelihood-${option.value}`" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="risks-modal-label">Impact</label>
            <select v-model.number="risk_form.residual_impact" class="risks-modal-input risks-modal-select">
              <option v-for="option in impact_options" :key="`residual-impact-${option.value}`" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="risks-modal-footer">
        <button type="button" class="btn btn-link text-decoration-none risks-modal-cancel" @click="close_risk_modal">Cancel</button>
        <button type="button" class="btn config-navbar-primary-action risks-modal-create" @click="create_risk">
          <i class="bi bi-plus-lg"></i>
          <span>Create</span>
        </button>
      </div>
    </div>
  </div>
</div>
